// Editable prompt template for each changelog generation step.
//
// Both prompts are user-editable via the Prompts system (usedIn = key, global
// tenantId=null). What is editable is the *editorial* half: how a change is
// described, grouped, and judged. The runtime half — the commit list, the
// audience scope from changelog settings, the item cap — is appended in code by
// the worker, because those come from a deploy or from the Deploys tab settings
// rather than from an author. Gemini enforces the output shape with a
// structured-output schema, so editing tunes the writing but cannot break
// parsing.
//
// These defaults are BOTH the worker fallback (when no prompt row exists) AND
// the seeded v1 content. Single source of truth.

export const CHANGELOG_PROMPT_DEPLOY_NOTES = 'changelog.deploy-notes' as const;
export const CHANGELOG_PROMPT_ROUNDUP = 'changelog.roundup' as const;

export type ChangelogPromptKey =
  | typeof CHANGELOG_PROMPT_DEPLOY_NOTES
  | typeof CHANGELOG_PROMPT_ROUNDUP;

export interface ChangelogPromptDef {
  key: ChangelogPromptKey;
  label: string;
  description: string;
  variables: readonly string[];
  defaultContent: string;
}

const DEPLOY_NOTES_RULES = `You are writing the release notes an internal admin reads to see what shipped in a Nexus production deploy.

Group every change you keep into exactly one section:
- "new" — functionality that did not exist before.
- "improved" — existing functionality made better: UX polish, performance, clearer copy, more capable versions of something that already worked.
- "fixed" — bugs, regressions, and reverts that repair broken behaviour.

Rules:
1. A conventional-commit prefix is the strongest signal and overrides your own reading: "feat:" is new, "fix:" is fixed, "perf:"/"refactor:" are improved when they change what a user experiences.
2. Write each description as one plain sentence about the effect on the person using Nexus, not a restatement of the commit subject. No trailing period.
3. Give every item a short "topic" label naming the area touched, the way a teammate would say it — "Opportunities", "Workspace header", "Admin events". Two or three words at most, no colon.
4. Copy the ticket key exactly as it appears in the input, or use null when the change has none. Never invent one.
5. Merge entries that describe the same shipped change into one item.
6. Write a "headline": one sentence naming what this deploy is mostly about, the way you would summarise it to a colleague. Lead with the largest change. End with a period.
7. Set "significance": "big-news" only when this deploy ships something a person using Nexus would notice and care about without being told — a new capability, a visible redesign, a change to how they work. Bug fixes, polish, copy tweaks, performance work, and internal changes are "small-update", however many of them there are. When in doubt, choose "small-update": announcing too little is recoverable, spamming every user is not.`;

const ROUNDUP_RULES = `You are writing a roundup of everything Nexus shipped over several deploys, for a reader who uses the product but does not work on it — a product manager, a designer, an account lead.

You are given the individual deploy notes an admin already picked out as customer-relevant. Turn them into one short, coherent summary.

Group every change you keep into exactly one section:
- "new" — functionality that did not exist before.
- "improved" — existing functionality made better: UX polish, performance, clearer copy, more capable versions of something that already worked.
- "fixed" — bugs and regressions that repair broken behaviour.

Rules:
1. Collapse items describing the same change across deploys into a single statement. A shared ticket key is the strongest signal: the same key appearing in three deploys is one change delivered in stages, not three changes. Describe the finished state, not the increments.
2. Also merge items that share no ticket but plainly describe the same feature arriving piece by piece.
3. Rewrite for someone who does not know the codebase. Say what they can now do, or what stopped going wrong — the outcome, never the mechanism. No file names, no service names, no internal jargon.
4. Drop anything a user would not notice, even though it was picked. Being handed an item is not an instruction to keep it.
5. Prefer omission over padding. A short roundup is a good roundup; five sharp lines beat fifteen dull ones.
6. Give every item a short "topic" label naming the area, the way a colleague would say it — "Opportunities", "Workspace header". Two or three words at most, no colon.
7. Put every source item id you merged into that item in "sourceItemIds". Use the ids exactly as given. An item you dropped appears in no list.
8. Copy a ticket key exactly as it appears in the input, or use null. When you merge several items sharing one key, keep it; when they disagree, use null. Never invent one.
9. Write each description as one plain sentence, no trailing period.
10. Write a "headline": one sentence naming what this stretch of work was mostly about, the way you would summarise it to a colleague. End with a period.`;

export const CHANGELOG_PROMPTS: readonly ChangelogPromptDef[] = [
  {
    key: CHANGELOG_PROMPT_DEPLOY_NOTES,
    label: 'Changelog — Deploy notes',
    description:
      'Turns a production deploy’s commits into the New / Improved / Fixed items the Admin Deploys tab shows.',
    variables: [],
    defaultContent: DEPLOY_NOTES_RULES,
  },
  {
    key: CHANGELOG_PROMPT_ROUNDUP,
    label: 'Changelog — Roundup',
    description:
      'Merges the deploy items an admin picked into one customer-facing roundup, for the Admin Roundups tab.',
    variables: [],
    defaultContent: ROUNDUP_RULES,
  },
];

/** Worker fallback when no prompt row exists yet, keyed by `usedIn`. */
export const CHANGELOG_PROMPT_DEFAULTS: Record<string, string> = Object.fromEntries(
  CHANGELOG_PROMPTS.map((p) => [p.key, p.defaultContent]),
);
